import React, { useState } from "react";
import { User, MapPin, Edit2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import ProfileCompletionCard from "../../components/ProfileCompletionCard";
import { fmtDateTime } from "../../lib/ist";
import { countryOf } from "../../lib/countries";
import { setUser as setStoredUser } from "../../lib/api";

// Lazily reuse the same modal from ProfileCompletionCard by rendering it
// via a small local wrapper. Keeps a single source of truth for the form.
import { api } from "../../lib/api";
import { COUNTRIES, CHARGEABLE_CURRENCIES } from "../../lib/countries";
import { Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, setUser } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  if (!user) return null;
  const b = user.billing || {};
  const country = b.country ? countryOf(b.country) : null;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>My Account</h2>

      <ProfileCompletionCard/>

      <div className="card p-6" data-testid="account-profile">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-300"><User size={22}/></div>
            <div>
              <div className="text-white font-semibold text-lg">{user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user.name}</div>
              <div className="text-xs text-slate-400">{user.email}</div>
            </div>
          </div>
          <button
            onClick={()=>setEditOpen(true)}
            className="btn-ghost !py-2 !px-3 text-xs inline-flex items-center gap-1.5"
            data-testid="edit-profile-btn"
          >
            <Edit2 size={12}/> Edit
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Info label="Role" value={<span className="capitalize">{user.role}</span>}/>
          <Info label="Phone" value={user.phone || "—"}/>
          <Info label="Company" value={user.company || "—"}/>
          <Info label="Preferred currency" value={user.preferred_currency || "—"}/>
          <Info label="Member since" value={fmtDateTime(user.created_at)}/>
          <Info label="Email verified" value={user.email_verified ? "Yes" : "Not yet"}/>
        </div>

        <div className="mt-6 pt-5 border-t border-white/5">
          <div className="text-slate-400 text-xs uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <MapPin size={12}/> Billing address
          </div>
          {b.address_line1 ? (
            <div className="text-slate-200 text-sm leading-relaxed" data-testid="account-billing">
              {b.address_line1}{b.address_line2 ? `, ${b.address_line2}` : ""}<br/>
              {[b.city, b.state, b.postal_code].filter(Boolean).join(", ")}<br/>
              {country ? `${country.flag} ${country.name}` : b.country}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No billing address on file — <button onClick={()=>setEditOpen(true)} className="text-cyan-400 underline">add one</button> to pre-fill your next checkout.</p>
          )}
        </div>
      </div>

      <ChangePasswordCard/>

      {editOpen && (
        <EditProfileModal user={user} onClose={()=>setEditOpen(false)} onSaved={(u)=>{ setStoredUser(u); setUser(u); setEditOpen(false); }}/>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-slate-500 text-xs">{label}</div>
      <div className="text-slate-200 mt-0.5">{value}</div>
    </div>
  );
}

// A self-contained edit modal — same fields as ProfileCompletionCard's inner
// modal, but exposed here for anytime editing from the Account page.
function EditProfileModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name:  user.last_name  || "",
    phone:      user.phone      || "",
    billing_country: (user.billing?.country) || "IN",
    preferred_currency: user.preferred_currency || "INR",
    address_line1: user.billing?.address_line1 || "",
    address_line2: user.billing?.address_line2 || "",
    city:          user.billing?.city          || "",
    state:         user.billing?.state         || "",
    postal_code:   user.billing?.postal_code   || "",
    company:       user.company                || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", form);
      toast.success("Profile saved");
      onSaved(data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="edit-profile-modal">
      <div className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="edit-profile-close">
          <X size={18}/>
        </button>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Edit profile</h2>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <F label="First name" v={form.first_name} on={set("first_name")} t="ep-first"/>
          <F label="Last name"  v={form.last_name}  on={set("last_name")}  t="ep-last"/>
          <F label="Phone" v={form.phone} on={set("phone")} t="ep-phone"/>
          <F label="Company" v={form.company} on={set("company")} t="ep-company"/>

          <div>
            <label className="label">Country</label>
            <select value={form.billing_country} onChange={set("billing_country")} className="input w-full" data-testid="ep-country">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Currency</label>
            <select value={form.preferred_currency} onChange={set("preferred_currency")} className="input w-full" data-testid="ep-currency">
              {CHARGEABLE_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <F label="Address line 1" v={form.address_line1} on={set("address_line1")} t="ep-addr1" span/>
          <F label="Address line 2" v={form.address_line2} on={set("address_line2")} t="ep-addr2" span/>
          <F label="City"  v={form.city}  on={set("city")}  t="ep-city"/>
          <F label="State" v={form.state} on={set("state")} t="ep-state"/>
          <F label="Postal / ZIP" v={form.postal_code} on={set("postal_code")} t="ep-postal"/>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={save} disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="ep-save">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Check size={16}/>}
            Save changes
          </button>
          <button onClick={onClose} className="btn-ghost" data-testid="ep-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, v, on, t, span = false }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="label">{label}</label>
      <input value={v} onChange={on} className="input w-full" data-testid={t}/>
    </div>
  );
}
